/**
 * Formatting, pinned to the platform's own timezone.
 *
 * "Timezone is display-only — all storage is UTC", and the platform's display
 * timezone is platform.timezone = Asia/Manila, which the sign-in screen states
 * outright ("All figures in PHP · Asia/Manila").
 *
 * Both formatters below MUST pin it. Left unpinned they fall back to the
 * browser's own zone, which puts an operator abroad — or on a laptop whose
 * clock is set to UTC — twelve hours away from the times their colleague is
 * reading, on the tables where that matters most: the sign-in trail, an admin's
 * login history and their recent privileged actions. The same instant already
 * renders as Asia/Manila on the system administration screen, so unpinned here
 * is also two screens of one console disagreeing about when something happened.
 */
export const DISPLAY_TZ = 'Asia/Manila';

const DAY = new Intl.DateTimeFormat('en-PH', {
  timeZone: DISPLAY_TZ,
  day: '2-digit',
  month: 'short',
});
const CLOCK = new Intl.DateTimeFormat('en-PH', {
  timeZone: DISPLAY_TZ,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** "29 Aug 08:12" — the login-history and audit stamp. */
export function stamp(iso: string): string {
  const d = new Date(iso);
  return `${DAY.format(d)} ${CLOCK.format(d)}`;
}

/** "2m ago" · "1h ago" · "12d ago" — the Last active column. */
export function relative(iso: string): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/** Avatar initials. First and last word only — a middle name is not an initial. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1] ?? '') : '';
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || '?';
}

/** "All brands" is the ABSENCE of scope rows, so an empty array is permissive. */
export function scopeLabel(brands: string[]): string {
  return brands.length === 0 ? 'All brands' : brands.join(' · ');
}
